/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import {Device} from '@ion/core/src/types/entities';
import useVehicleProfileStatus from '@ion/query/src/hooks/useVehicleProfileStatus';
import {getVin} from './state';
import {useDeviceRecord} from './useDeviceRecord';

export interface UseDeviceVehicleProfileParams {
  deviceId?: number | string;
  initialDevice?: Device;
  initialDeviceDetail?: Device;
}

export type UseDeviceVehicleProfileResult = {
  vin: string;
  isTestVin?: boolean;
  hasProfile: boolean;
  isLoading: boolean;
  isError: boolean;
  refresh: () => void;
};

/**
 * The useDeviceVehicleProfile hook derives the VIN and vehicle-profile
 * environment the Device Details dialog displays from the shared device
 * record, so the dialog no longer depends on a parent grid having loaded the
 * Vehicle Profile or VIN ID Report columns.
 *
 * State tracked is the resolved VIN and the raw `isTestVin` result of the VIN
 * profile lookup; it changes whenever the shared device query resolves, the
 * VIN changes, or the lookup is refreshed. Both gates are data gates: the
 * device query runs once a `deviceId` is available, and the profile lookup
 * runs once a VIN is known. Permissions are deliberately not an input here;
 * the hook answers what the current value is, and the caller separately
 * decides whether an action is allowed.
 *
 * The raw `isTestVin` boolean is returned rather than a display label, so the
 * caller owns translation and no comparison is made against translated text.
 *
 * `initialDevice` and `initialDeviceDetail` are placeholders that let the
 * profile lookup start a render earlier; they are never the authority.
 *
 * ```typescript
 * import {useDeviceVehicleProfile} from './useDeviceVehicleProfile';
 *  ...
 *  const profile = useDeviceVehicleProfile({deviceId, initialDevice: device});
 * ```
 *
 * @param deviceId            of the device to read the vehicle profile for
 * @param initialDevice       already-loaded device used only to seed the VIN
 * @param initialDeviceDetail already-loaded detail used only to seed the VIN
 * @returns the VIN, profile environment, load state, and a refresh function
 */
export const useDeviceVehicleProfile = ({
  deviceId,
  initialDevice,
  initialDeviceDetail
}: UseDeviceVehicleProfileParams): UseDeviceVehicleProfileResult => {
  const {
    device,
    isLoading : isDeviceLoading,
    isError   : isDeviceError
  } = useDeviceRecord({deviceId, fallbacks: [initialDevice, initialDeviceDetail]});

  const vin    = getVin(device);
  const hasVin = !isEmpty(vin);

  const {
    isTestVin,
    isLoading : isProfileLoading,
    isError   : isProfileError,
    refresh
  } = useVehicleProfileStatus(vin);

  return {
    vin,
    isTestVin,
    hasProfile : !isUndefined(isTestVin),
    isLoading  : hasVin ? isProfileLoading : isDeviceLoading,
    isError    : isDeviceError || (hasVin && isProfileError),
    refresh
  };
};

export default useDeviceVehicleProfile;
